import { Box, Typography } from "@mui/material"
import { UploadFileOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useId, useRef } from "react"

import type { MimeType } from "@/components/upload/constants"
import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import { FilePreview } from "@/components/upload/SingleFileUpload"
import { convertMimeTypesToAccept } from "@/components/upload/utils"
import { useDragAndDrop } from "@/hooks/useDragAndDrop"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT = convertMimeTypesToAccept(IMAGE_MIME_TYPES_WITH_PDF_AND_DOC)

interface DocumentAttachmentsSectionProps {
  files: File[]
  onAdd: (file: File) => void
  onRemove: (index: number) => void
  fileInputRef?: React.RefObject<HTMLInputElement>
}

export const DocumentAttachmentsSection: FC<DocumentAttachmentsSectionProps> = ({
  files,
  onAdd,
  onRemove,
  fileInputRef,
}) => {
  const internalRef = useRef<HTMLInputElement>(null)
  const effectiveRef = fileInputRef ?? internalRef
  const baseId = useId()
  const fileIdCounter = useRef(0)
  const fileIds = useRef<string[]>([])
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const validateAndAdd = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: `${file.name} exceeds the 10MB file size limit`,
        })
        return
      }
      if (!IMAGE_MIME_TYPES_WITH_PDF_AND_DOC.includes(file.type as MimeType)) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: `${file.name} is not a supported file type. Allowed: ${ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}`,
        })
        return
      }
      fileIds.current = [...fileIds.current, `${baseId}-${fileIdCounter.current++}`]
      onAdd(file)
    },
    [notify, onAdd, baseId]
  )

  const { dropRef, isOver } = useDragAndDrop({ onDrop: validateAndAdd, enabled: true })

  const handleClick = useCallback(() => {
    effectiveRef.current?.click()
  }, [effectiveRef])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) validateAndAdd(file)
      event.target.value = ""
    },
    [validateAndAdd]
  )

  return (
    <Box className='flex flex-col gap-3'>
      <Typography variant='body2' className='font-medium text-gray-700'>
        Document Attachments
      </Typography>

      {files.map((file, index) => (
        <Box key={fileIds.current[index] ?? `${index}`} className='rounded-lg border border-gray-200'>
          <FilePreview
            file={file}
            onRemove={() => {
              fileIds.current = fileIds.current.filter((_, i) => i !== index)
              onRemove(index)
            }}
            data-testid={`gl-transaction.form-dialog.attachment-preview-${index}`}
          />
        </Box>
      ))}

      <input
        ref={effectiveRef}
        type='file'
        accept={ACCEPT}
        onChange={handleInputChange}
        style={{ display: "none" }}
        data-testid='gl-transaction.form-dialog.file-input'
      />
      <Box
        ref={dropRef}
        role='button'
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleClick()
          }
        }}
        className={`flex h-[100px] w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed outline-none transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          isOver ? "border-primary bg-blue-50" : "border-gray-300 hover:border-primary hover:bg-gray-50"
        }`}
        data-testid='gl-transaction.form-dialog.file-drop-zone'
      >
        <UploadFileOutlined className='text-primary' />
        <Typography variant='body2' className='text-center text-gray-600'>
          <Box component='span' className='font-medium text-primary underline'>
            Click to upload
          </Box>{" "}
          or drag and drop
        </Typography>
        <Typography variant='caption' className='text-gray-400'>
          {ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC} (max. 10MB)
        </Typography>
      </Box>
    </Box>
  )
}
